import { useCallback, useMemo } from "react";

import { useNextWorkflowOrderAction } from "../components/NextWorkflowsEditor/hooks";
import { useTaskOrderAction } from "../components/TasksEditor/hooks";
import { DND_TYPE } from "./useDragDropEventManager";

const toFlatItem = (itemType) => {
  return (tg) => {
    return tg?.[itemType] && tg?.conditions?.length ? tg?.[itemType] : tg;
  };
};

export function useConditionalItemOrderManager(
  itemGroups,
  updateItem,
  onDone = () => {},
  loading = false,
  itemType = "task"
) {
  const {
    running: runningTask,
    reOrderTask,
    assignTaskToConditionsGroup,
    removeTaskFromConditionsGroup,
  } = useTaskOrderAction();

  const {
    running: runningNwf,
    reOrderNextWorkflow,
    assignNextWorkflowToConditionsGroup,
    removeNextWorkflowFromConditionsGroup,
  } = useNextWorkflowOrderAction();

  const actionDecider = (type) => {
    switch (type) {
      case DND_TYPE.REORDER:
        return itemType === "task" ? reOrderTask : reOrderNextWorkflow;
      case DND_TYPE.DRAG_TO_CONDITIONS_GROUP:
        return itemType === "task"
          ? assignTaskToConditionsGroup
          : assignNextWorkflowToConditionsGroup;
      case DND_TYPE.DRAG_OUT_OF_CONDITIONS_GROUP:
        return itemType === "task"
          ? removeTaskFromConditionsGroup
          : removeNextWorkflowFromConditionsGroup;
      default:
        return async () => {};
    }
  };

  const items = useMemo(() => {
    return (itemGroups || []).flatMap(toFlatItem(itemType));
  }, [itemGroups, itemType]);

  const lastOrder = useMemo(() => {
    return items?.length ? items[items.length - 1].order : 0;
  }, [items]);

  const isRunning = useCallback(() => {
    return (itemGroups || []).filter(({ conditions }) => {
      return conditions?.length;
    }).length
      ? runningTask || loading || runningNwf
      : false;
  }, [itemGroups, runningTask, runningNwf, loading]);

  return {
    running: isRunning(),
    items: items,
    lastOrder: lastOrder,
    reorder: async ({ eventType, itemGroups: orderedList, ...props }) => {
      updateItem({ type: "set", value: orderedList });

      const action = actionDecider(eventType);
      await action(
        { orderedItems: orderedList.flatMap(toFlatItem(itemType)), ...props },
        items
      );

      onDone();
    },
  };
}

export default useConditionalItemOrderManager;
